import { IconTypes } from '@stockmarkteam/donguri-ui';
import type {
  NotificationGroupingName,
  NotificationItem,
  NotificationType,
  Perspective,
} from '@/types';
import { DateRangePreset } from '@/types/index';
import {
  getADayAgo,
  getAHalfYearAgo,
  getAMonthAgo,
  getAWeekAgo,
  getAYearAgo,
  getThreeMonthsAgo,
  getToday,
  getTwoYearsAgo,
} from '@/utils/dates';
import aconnectLogo from './assets/logo_aconnect.png';

export const PRODUCT_NAME = 'Aconnect';
export const PRODUCT_LOGO_ASSET = aconnectLogo;
export const PRODUCT_LOGO_STYLE = { height: '26px', width: '120px' };
export const PRODUCT_ICON_NAME: Extract<IconTypes, 'aconnect-logo'> =
  'aconnect-logo';
export const MAX_EMAIL_LENGTH = 255;
export const MAX_NAME_LENGTH_FOR_COMMENT = 15;
export const MAX_NAME_LENGTH = 50;
export const MAX_COMMENT_LENGTH = 500;
export const MAX_THEME_NAME_LENGTH = 50;
export const MAX_DESCRIPTION_LENGTH = 50;
export const MAX_SITE_EXCLUSIONS = 100;
export const MAX_KEYWORDS_LENGTH = 2000;
export const MAX_KEYWORDS_LENGTH_BUFFER = 100;
export const MAX_GROUP_NAME_LENGTH = 20;
export const MAX_GROUP_DESCRIPTION_LENGTH = 500;
export const DAYS_OF_PAST_FEED = 7;
export const SITES_COUNT_PER_REQUEST = 100;
export const SITE_API_DEBOUNCE_WAIT_MS = 500;
export const SEARCH_API_DEBOUNCE_WAIT_MS = 700;
export const FILE_NAME_SEARCH_API_DEBOUNCE_WAIT_MS = 300;
export const SURVEY_SEARCH_DEBOUNCE_WAIT_MS = 300;
export const PATENT_FTO_PROJECTS_SEARCH_DEBOUNCE_WAIT_MS = 300;
export const ARTICLE_SHARE_DEBOUNCE_WAIT_MS = 300;
export const IOS_STORE_URL = 'https://apps.apple.com/jp/app/id1530143368';
export const ANDROID_STORE_URL =
  'https://play.google.com/store/apps/details?id=ai.stockmark.anews';
export const MAX_TOOLTIP_LIST_LENGTH = 10;
export const MAX_ARTICLE_MEMO_LENGTH = 1000;
// 1記事あたり、タグの登録は10件までとする
export const MAX_ARTICLE_TAG_LENGTH = 10;
// 登録するタグ名は以下定義の文字数までとする
export const MAX_TAG_NAME_LENGTH = 20;

export const TOOLTIP_FOR_TEAM_FEATURED_ARTICLES =
  '過去7日間のマークを集計しています。';
export const TOOLTIP_FOR_RELATED_NEWS =
  'キーワード一致では拾えない、キーワードに近い内容やテーマ内のアクション履歴に関連性のあるニュースが表示されます。';
export const DAY_OF_WEEK_LIST = ['日', '月', '火', '水', '木', '金', '土'];

export const NON_THEME_PERSPECTIVE: Perspective = {
  id: 1,
  category: '',
  question: '目的を限定せずに幅広く情報を取りたい',
  name: '',
  description: '',
  query_ja: '',
  query_en: '',
};
//コメント作成モーダルで表示する既存コメントの数
export const DISPLAY_LATEST_COMMENTS_COUNT = 3;
export const INITIAL_VISIBLE_DOCUMENT_COUNT = 20;
export const THEME_RELATED_NEWS_MAX_COUNT = 10;
export const MAX_REACTION_TYPE_COUNT = 20;
export const SUPPORT_UNICODE_VERSION = 12.0;
export const KEYWORD_RECOMMEND_DEBOUNCE_TIME = 500;
export const THEME_PREVIEW_DEBOUNCE_TIME = 500;
export const KEYWORD_COMPLETION_DEBOUNCE_TIME = 300;

export const UNEXPECTED_CONTENT_TYPE_ERROR =
  'response content-type is not JSON';

export const USER_MARK_SHARE = 'マーク：情報を蓄積する';
export const GROUP_MARK_SHARE = 'グループマーク：グループに情報を共有する';
export const COMMENT_SHARE = 'コメントする';

export const EMPTY_KEYWORD_FEED_MESSAGE = '新着ニュースはありませんでした。';

export const STOCKMARK_LABS_HELP_URL =
  'https://anews-stockmark.zendesk.com/hc/ja/categories/34461136067353';
// リクエスト送信ページへのリンク
export const SEND_REQUEST_PAGE_URL =
  'https://anews-stockmark.zendesk.com/hc/ja/requests/new';

// 問い合わせメールリンク
export const CONTACT_MAIL_LINK =
  'mailto:stockmark_labs@stockmark.co.jp?subject=【Stockmark Labs/調査AIエージェント】お問い合わせ&body=*内容を追記し、ご送付ください。%0D%0Aご質問内容：%0D%0Aこちらに質問を記載ください。%0D%0Aできるだけ詳細に頂けますと幸いです。';
export const SERVICE_START_DATE = new Date(2020, 5, 19);
export const FIRST_PUBLISHED_AT = new Date(2019, 0, 1);
export const SEARCH_DOC_TYPE_FIRST_PUBLISHED_AT = {
  all: new Date(1995, 0, 1),
  news: new Date(2019, 0, 1),
  research_paper: new Date(1995, 0, 1),
  patent: new Date(2005, 0, 1),
  report: new Date(2019, 0, 1),
};
export const OLDEST_CALENDAR_DATE = new Date(1900, 0, 1);
export const presetDates: DateRangePreset[] = [
  {
    key: 'all',
    label: 'すべての期間',
    range: {
      from: SERVICE_START_DATE,
      to: getToday(),
    },
  },
  {
    key: '1m',
    label: '過去1ヶ月',
    range: {
      from: getAMonthAgo(),
      to: getToday(),
    },
  },
  {
    key: '3m',
    label: '過去3ヶ月',
    range: {
      from: getThreeMonthsAgo(),
      to: getToday(),
    },
  },
  {
    key: '6m',
    label: '過去6ヶ月',
    range: {
      from: getAHalfYearAgo(),
      to: getToday(),
    },
  },
  {
    key: '1y',
    label: '過去1年',
    range: {
      from: getAYearAgo(),
      to: getToday(),
    },
  },
];
export const presetADayAgoDates: DateRangePreset[] = [
  {
    key: 'all',
    label: 'すべての期間',
    range: {
      from: getTwoYearsAgo(),
      to: getADayAgo(),
    },
  },
  {
    key: '1m',
    label: '過去1ヶ月',
    range: {
      from: getAMonthAgo(),
      to: getADayAgo(),
    },
  },
  {
    key: '3m',
    label: '過去3ヶ月',
    range: {
      from: getThreeMonthsAgo(),
      to: getADayAgo(),
    },
  },
  {
    key: '6m',
    label: '過去6ヶ月',
    range: {
      from: getAHalfYearAgo(),
      to: getADayAgo(),
    },
  },
  {
    key: '1y',
    label: '過去1年',
    range: {
      from: getAYearAgo(),
      to: getADayAgo(),
    },
  },
];
export const searchPresetDates: DateRangePreset[] = [
  {
    key: 'all',
    label: 'すべての期間',
    range: {
      from: undefined,
      to: undefined,
    },
  },
  {
    key: '1d',
    label: '過去24時間',
    range: {
      from: getADayAgo(),
      to: getToday(),
    },
  },
  {
    key: '1w',
    label: '過去1週間',
    range: {
      from: getAWeekAgo(),
      to: getToday(),
    },
  },
  {
    key: '1m',
    label: '過去1ヶ月',
    range: {
      from: getAMonthAgo(),
      to: getToday(),
    },
  },
  {
    key: '6m',
    label: '過去6ヶ月',
    range: {
      from: getAHalfYearAgo(),
      to: getToday(),
    },
  },
  {
    key: '1y',
    label: '過去1年',
    range: {
      from: getAYearAgo(),
      to: getToday(),
    },
  },
];

export const userDocumentSearchPresetDates: DateRangePreset[] = [
  {
    key: 'all',
    label: 'すべての期間',
    range: {
      from: undefined,
      to: undefined,
    },
  },
  {
    key: '1d',
    label: '過去24時間',
    range: {
      from: getADayAgo(),
      to: getToday(),
    },
  },
  {
    key: '1w',
    label: '過去1週間',
    range: {
      from: getAWeekAgo(),
      to: getToday(),
    },
  },
  {
    key: '1m',
    label: '過去1ヶ月',
    range: {
      from: getAMonthAgo(),
      to: getToday(),
    },
  },
  {
    key: '6m',
    label: '過去6ヶ月',
    range: {
      from: getAHalfYearAgo(),
      to: getToday(),
    },
  },
  {
    key: '1y',
    label: '過去1年',
    range: {
      from: getAYearAgo(),
      to: getToday(),
    },
  },
];

// アクティビティの初期検索条件
// NOTE: OpenSearchに対して期間の絞り込みを行うと外部リンク共有した記事が表示されなくなるため
// 期間で絞り込みを行っていないデフォルト値である「すべての期間」は値をundefinedにしている
export const searchPresetActivity: DateRangePreset[] = [
  {
    key: 'all',
    label: 'すべての期間',
    range: {
      from: undefined,
      to: undefined,
    },
  },
  {
    key: '1d',
    label: '過去24時間',
    range: {
      from: getADayAgo(),
      to: getToday(),
    },
  },
  {
    key: '1w',
    label: '過去1週間',
    range: {
      from: getAWeekAgo(),
      to: getToday(),
    },
  },
  {
    key: '1m',
    label: '過去1ヶ月',
    range: {
      from: getAMonthAgo(),
      to: getToday(),
    },
  },
  {
    key: '6m',
    label: '過去6ヶ月',
    range: {
      from: getAHalfYearAgo(),
      to: getToday(),
    },
  },
  {
    key: '1y',
    label: '過去1年',
    range: {
      from: getAYearAgo(),
      to: getToday(),
    },
  },
];

export const MAX_ORGANIZATION_TAG_NAME_LENGTH = 40;
export const TECH_EXPLORATION_NOTIFICATION_GROUPINGS = {
  techExplorationMentions: ['tech_exploration_mention'] as const,
  techExplorationReviewRequests: ['tech_exploration_review_request'] as const,
} as const;
export const PATENT_FTO_NOTIFICATION_GROUPINGS = {
  patentFtoAnalysisCompletions: ['patent_fto_analysis_completion'] as const,
} as const;

export const NOTIFICATION_TYPE: {
  [key in Uppercase<NotificationItem['type']>]: Lowercase<key>;
} = {
  MENTION: 'mention',
  REPLY_COMMENT: 'reply_comment',
  COMMENT_REACTION: 'comment_reaction',
  GROUP_INVITATION: 'group_invitation',
  FIRST_PERSONAL_NEWS: 'first_personal_news',
  THEME_INVITATION: 'theme_invitation',
  UPDATE_THEME_FEED: 'update_theme_feed',
  FORCE_THEME_FAVORITE: 'force_theme_favorite',
  GROUP_MARK: 'group_mark',
  GROUP_COMMENT: 'group_comment',
  SURVEY_HISTORY: 'survey_history',
  SHARE_SURVEY: 'share_survey',
  PATENT_FTO_ANALYSIS_COMPLETION: 'patent_fto_analysis_completion',
  TECH_EXPLORATION_REVIEW_REQUEST: 'tech_exploration_review_request',
  TECH_EXPLORATION_MENTION: 'tech_exploration_mention',
};

export const NOTIFICATION_GROUPINGS = {
  mentions: [NOTIFICATION_TYPE.MENTION, NOTIFICATION_TYPE.REPLY_COMMENT],
  members: [
    NOTIFICATION_TYPE.GROUP_INVITATION,
    NOTIFICATION_TYPE.COMMENT_REACTION,
    NOTIFICATION_TYPE.THEME_INVITATION,
    NOTIFICATION_TYPE.FORCE_THEME_FAVORITE,
    NOTIFICATION_TYPE.GROUP_MARK,
    NOTIFICATION_TYPE.GROUP_COMMENT,
    NOTIFICATION_TYPE.SHARE_SURVEY,
  ],
  others: [
    NOTIFICATION_TYPE.FIRST_PERSONAL_NEWS,
    NOTIFICATION_TYPE.UPDATE_THEME_FEED,
    NOTIFICATION_TYPE.SURVEY_HISTORY,
    NOTIFICATION_TYPE.PATENT_FTO_ANALYSIS_COMPLETION,
  ],
} satisfies Record<NotificationGroupingName, NotificationType[]>;

export const MAX_IP_ADDRESS_RESTRICTION_DESCRIPTION = 20;
export const MAX_IP_ADDRESS_RESTRICTIONS = 50;
export const MAX_IP_ADDRESS_RESTRICTION_FORM = 10;

// search expanded queryのキャッシュ期間(ms)
export const SEARCH_EXPANDED_QUERY_CACHE_EXPIRATION = 1000 * 60 * 60 * 24 * 7; // 1 week

// expanded queryのindexeddbストア名
export const SEARCH_EXPANDED_QUERY_CACHE_STORE_NAME = 'searchExpandedQueries';
export const USER_DOCUMENT_SEARCH_EXPANDED_QUERY_CACHE_STORE_NAME =
  'userDocumentSearchExpandedQueries';

// sharepoint登録エラー
export const SHAREPOINT_ERROR_MESSAGES: { [key: string]: string } = {
  'credential is expired': '認証情報が期限切れです',
  'required role is missing': '必要な権限がありません',
  'invalid credential': '認証情報が無効です',
  'site not found': 'サイトが見つかりません',
  'invalid site url': 'サイトのURLが無効です',
  'token type is not allowed': 'SharePointへの連携が許可されていません',
  'sharepoint rest forbidden': 'SharePointへのアクセス権限が設定されていません',
  'required sharepoint rest api permission is missing':
    'SharePointへのアクセスに必要な権限がありません',
};

// contractの上限エラー
export const CONTRACT_UPPER_LIMIT_ERROR_MESSAGES: { [key: string]: string } = {
  all_upper_limit:
    'すべての契約が上限に達しています。\nメンバーを追加する場合は、不要なメンバーの削除、またはサポート担当までご連絡下さい。',
  upper_limit:
    'この契約は上限に達しています。\nこの契約をお選びいただく場合は、該当契約に紐づいている不要なメンバーを削除、またはサポート担当までご連絡下さい。',
};

/**
 * ### PESTの各カテゴリーの配列
 * - 以下の4つのカテゴリーに対応する要素を持つ
 *   - 政治: politics
 *   - 経済: economy
 *   - 社会: society
 *   - 技術: technology
 */
export const pests = ['politics', 'economy', 'society', 'technology'] as const;

export const pestNames = {
  politics: '政治カテゴリー',
  economy: '経済カテゴリー',
  society: '社会カテゴリー',
  technology: '技術カテゴリー',
} as const satisfies Record<(typeof pests)[number], string>;

export const DOC_TYPE_LABELS = {
  article: 'ニュース',
  research_paper: '論文',
  patent: '特許',
  report: 'レポート',
  user_document: '社内情報',
} as const;

// 社内情報プレビュー用画像生成用テンプレートのページ番号プレースホルダ
export const PAGE_NUMBER_PLACEHOLDER = encodeURIComponent('{page_number}');

// 登録できるBoxフォルダURLの最大数
export const MAX_BOX_FOLDER_URLS = 50;

// 登録できるSharepointサイトURLの最大数
export const MAX_SHAREPOINT_SITE_URLS = 50;

export const FUNCTION_KEYS = [
  'information_retrieval',
  'forced_ideation',
  'market_sizing',
  'tech_exploration',
  'patent_fto',
  'fmea',
] as const;

export const AGENT_NAME_INFORMATION_RETRIEVAL = '情報収集エージェント';
export const AGENT_TOP_ROUTE_NAME_INFORMATION_RETRIEVAL = 'anewsHome'; // 情報収集エージェントのトップページのroute名

export const AGENT_NAME_PATENT_FTO = '特許調査エージェント';
export const AGENT_TOP_ROUTE_NAME_PATENT_FTO = 'patentFtoAnalyses'; // 特許調査エージェントのトップページのroute名
export const PATENT_FTO_SUPPORT_URL =
  'https://anews-stockmark.zendesk.com/hc/ja/articles/51389428718617'; // 特許調査エージェントのヘルプページURL
// 特許調査エージェントの調査対象アップロードにおける対応フォーマット確認用URL
// NOTE: サポートページの該当フォーマットセクションに直接遷移するため`PATENT_FTO_SUPPORT_URL`とは別の定数としてアンカーリンクにしている
export const PATENT_FTO_FORMAT_SUPPORT_URL =
  'https://anews-stockmark.zendesk.com/hc/ja/articles/51389428718617#h_01KBHKH1TDAN1WY7PPXVJH0PXT';

export const AGENT_NAME_TECH_EXPLORATION = '技術探索エージェント';
export const AGENT_TOP_ROUTE_NAME_TECH_EXPLORATION = 'techExplorationTop'; // 技術探索エージェントのトップページのroute名

export const TECH_EXPLORATION_EMPTY_TITLE = '未生成の探索';
export const TECH_EXPLORATION_MAX_USER_CREATED_ITEMS = 3;
export const TECH_EXPLORATION_COMPARISON_DISABLED_TOOLTIP = (action: string) =>
  `AI生成が完了すると${action}できます`;

export const AGENT_NAME_FORCED_IDEATION = '強制発想法エージェント';
export const AGENT_TOP_ROUTE_NAME_FORCED_IDEATION = 'forcedIdeationNew';
export const FORCED_IDEATION_SUPPORT_URL =
  'https://anews-stockmark.zendesk.com/hc/ja/articles/59484988087321'; // 強制発想法エージェントのヘルプページURL

export const AGENT_NAME_MARKET_SIZING = '市場規模推定エージェント';
export const AGENT_TOP_ROUTE_NAME_MARKET_SIZING = 'marketSizingNew';
export const MARKET_SIZING_SUPPORT_URL =
  'https://anews-stockmark.zendesk.com/hc/ja/articles/59485012544921'; // 市場規模推定エージェントのヘルプページURL

export const AGENT_NAME_FMEA = 'FMEAエージェント';
export const AGENT_TOP_ROUTE_NAME_FMEA = 'fmea';
